import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { generateUniqueUsername } from '../../utils/username';

const ProfileSetup = () => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    userType: 'learner',
    bio: '',
    skills: [],
    hourlyRate: '',
    availability: '',
    languages: [],
    experience: '',
    expertise: [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Auto-detect user's timezone
    profilePicture: '',
    videoIntroUrl: '',
    certifications: [],
    testimonials: []
  });

  // Extract Google profile data when component mounts
  useEffect(() => {
    if (currentUser) {
      // Check if user signed in with Google
      const isGoogleUser = currentUser.providerData?.some(provider => provider.providerId === 'google.com');
      
      console.log('Current user:', currentUser);
      console.log('Is Google user:', isGoogleUser);
      console.log('Photo URL:', currentUser.photoURL);
      
      if (isGoogleUser) {
        const displayName = currentUser.displayName || '';
        const nameParts = displayName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const photoURL = getHighQualityGooglePhoto(currentUser.photoURL);
        
        console.log('Original photo URL:', currentUser.photoURL);
        console.log('High quality photo URL:', photoURL);
        
        setFormData(prev => ({
          ...prev,
          firstName,
          lastName,
          profilePicture: photoURL || '',
          originalPhotoURL: currentUser.photoURL || ''  // Store original for fallback
        }));
      }
    }
  }, [currentUser]);
  const [loading, setLoading] = useState(false);

  // Helper function to get high-quality Google profile picture
  const getHighQualityGooglePhoto = (photoURL) => {
    if (!photoURL) return '';
    
    console.log('Original Google photo URL:', photoURL);
    
    // Google Photos URLs often end with =s96-c, we can change this to =s400-c for higher resolution
    if (photoURL.includes('googleusercontent.com')) {
      // Remove size parameter and add high-res one
      const baseUrl = photoURL.split('=')[0];
      const highResUrl = `${baseUrl}=s400-c`;
      console.log('Modified URL for high resolution:', highResUrl);
      return highResUrl;
    }
    
    console.log('Non-Google URL, returning as-is:', photoURL);
    return photoURL;
  };

  const skillOptions = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 
    'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic',
    'Russian', 'Hindi', 'Dutch', 'Swedish', 'Norwegian'
  ];

  const expertiseOptions = [
    'Technology', 'Finance', 'Marketing', 'Sales', 'Healthcare',
    'Education', 'Law', 'Engineering', 'Design', 'Consulting',
    'Real Estate', 'Retail', 'Hospitality', 'Media', 'Non-profit'
  ];

  const timezoneOptions = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Dublin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Jakarta',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value) 
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleAddCertification = () => {
    const newCert = {
      id: Date.now(),
      name: '',
      issuer: '',
      year: '',
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, newCert]
    }));
  };

  const handleUpdateCertification = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.map(cert => 
        cert.id === id ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const handleRemoveCertification = (id) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(cert => cert.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Generate unique username from first and last name
      const username = await generateUniqueUsername(formData.firstName, formData.lastName);
      
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...formData,
        username, // Add username field
        email: currentUser.email,
        createdAt: new Date(),
        isProfileComplete: true,
        activeRole: formData.userType === 'both' ? 'learner' : formData.userType
      });
      
      window.location.reload();
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      // Check if user signed in with Google to get their info
      const isGoogleUser = currentUser.providerData?.some(provider => provider.providerId === 'google.com');
      let firstName = 'User';
      let lastName = '';
      let profilePicture = '';
      
      if (isGoogleUser) {
        const displayName = currentUser.displayName || '';
        const nameParts = displayName.split(' ');
        firstName = nameParts[0] || 'User';
        lastName = nameParts.slice(1).join(' ') || '';
        profilePicture = getHighQualityGooglePhoto(currentUser.photoURL) || '';
      }

      // Generate username for skipped profiles too
      const username = await generateUniqueUsername(firstName, lastName);
      
      await setDoc(doc(db, 'users', currentUser.uid), {
        firstName,
        lastName,
        username, // Add username field
        userType: 'learner',
        bio: 'New user',
        skills: [],
        languages: ['English'],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        profilePicture,
        email: currentUser.email,
        createdAt: new Date(),
        isProfileComplete: true,
        activeRole: 'learner'
      });
      
      window.location.reload();
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-setup">
      <div className="profile-setup-container">
        <h2>Complete Your Profile</h2>
        <form onSubmit={handleSubmit}>
          {formData.profilePicture && formData.profilePicture.trim() !== '' && (
            <div className="form-group profile-picture-section">
              <label>Profile Picture (from Google)</label>
              <div className="profile-picture-preview">
                <img 
                  src={formData.profilePicture} 
                  alt="Profile" 
                  className="preview-image"
                  onError={(e) => {
                    console.error('ProfileSetup: Image failed to load:', formData.profilePicture);
                    console.error('Error details:', e.type, e.target.src);
                    
                    // Try fallback to original URL if we have it and it's different
                    if (formData.originalPhotoURL && formData.originalPhotoURL !== formData.profilePicture) {
                      console.log('Trying fallback to original URL:', formData.originalPhotoURL);
                      e.target.src = formData.originalPhotoURL;
                      return;
                    }
                    
                    // Test if URL is accessible
                    fetch(formData.profilePicture, { method: 'HEAD', mode: 'no-cors' })
                      .then(() => console.log('URL is accessible via fetch'))
                      .catch(err => console.error('URL fetch failed:', err));
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    console.log('ProfileSetup: Image loaded successfully:', formData.profilePicture);
                    console.log('Image dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                  }}
                />
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                  URL: {formData.profilePicture}
                </div>
                <p>We've imported your Google profile picture!</p>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>I want to:</label>
            <select 
              name="userType" 
              value={formData.userType}
              onChange={handleInputChange}
            >
              <option value="learner">Practice conversations (learner)</option>
              <option value="provider">Provide conversation practice (earn money)</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div className="form-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder="Tell us about yourself..."
              rows="4"
              required
            />
          </div>

          {(formData.userType === 'provider' || formData.userType === 'both') && (
            <>
              <div className="form-group">
                <label>Skills I can help with:</label>
                <div className="checkbox-grid">
                  {skillOptions.map(skill => (
                    <label key={skill} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.skills.includes(skill)}
                        onChange={() => handleMultiSelect('skills', skill)}
                      />
                      {skill}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Hourly Rate ($)</label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  min="5"
                  max="500"
                  required
                />
              </div>

              <div className="form-group">
                <label>Experience/Qualifications</label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  placeholder="Describe your relevant experience, certifications, education, etc."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Professional Expertise:</label>
                <div className="checkbox-grid">
                  {expertiseOptions.map(area => (
                    <label key={area} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.expertise.includes(area)}
                        onChange={() => handleMultiSelect('expertise', area)}
                      />
                      {area}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Video Introduction URL (optional)</label>
                <input
                  type="url"
                  name="videoIntroUrl"
                  value={formData.videoIntroUrl}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                />
                <small>Upload a short video introducing yourself to potential clients</small>
              </div>

              <div className="form-group">
                <label>Certifications & Credentials</label>
                {formData.certifications.map(cert => (
                  <div key={cert.id} className="certification-item">
                    <div className="cert-row">
                      <input
                        type="text"
                        placeholder="Certification Name"
                        value={cert.name}
                        onChange={(e) => handleUpdateCertification(cert.id, 'name', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Issuing Organization"
                        value={cert.issuer}
                        onChange={(e) => handleUpdateCertification(cert.id, 'issuer', e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Year"
                        value={cert.year}
                        onChange={(e) => handleUpdateCertification(cert.id, 'year', e.target.value)}
                        min="1950"
                        max={new Date().getFullYear()}
                      />
                      <button 
                        type="button" 
                        onClick={() => handleRemoveCertification(cert.id)}
                        className="remove-cert-btn"
                      >
                        ×
                      </button>
                    </div>
                    <textarea
                      placeholder="Description (optional)"
                      value={cert.description}
                      onChange={(e) => handleUpdateCertification(cert.id, 'description', e.target.value)}
                      rows="2"
                    />
                  </div>
                ))}
                <button type="button" onClick={handleAddCertification} className="add-cert-btn">
                  + Add Certification
                </button>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Languages I speak:</label>
            <div className="checkbox-grid">
              {languageOptions.map(language => (
                <label key={language} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.languages.includes(language)}
                    onChange={() => handleMultiSelect('languages', language)}
                  />
                  {language}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Timezone</label>
            <select 
              name="timezone" 
              value={formData.timezone}
              onChange={handleInputChange}
              required
            >
              {timezoneOptions.map(tz => (
                <option key={tz} value={tz}>
                  {tz.replace('_', ' ')} ({(() => {
                    try {
                      return new Date().toLocaleTimeString('en-US', { 
                        timeZone: tz, 
                        hour12: false, 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      });
                    } catch (error) {
                      return 'Time unavailable';
                    }
                  })()})
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Saving...' : 'Complete Profile'}
            </button>
            <button 
              type="button" 
              onClick={handleSkip} 
              disabled={loading} 
              className="skip-btn"
            >
              Skip for Now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;